import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import { useProductivityAdjustmentApprovalScreen } from './hooks/useProductivityAdjustmentApprovalScreen'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import ApprovalEmptyState from '../components/ApprovalEmptyState'
import { formatApprovalDateTime, productivityStatusChipClass } from './utils/productivityApprovalPresentation'
import ProductivityAdjustmentEditModal from './components/ProductivityAdjustmentEditModal'

export default function ProductivityAdjustmentApprovalScreen() {
  const { state, actions } = useProductivityAdjustmentApprovalScreen()
  const location = useLocation()
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, remark: '' })
  const [editItem, setEditItem] = useState(null)

  async function submitReject() {
    if (!rejectModal.id) return
    await actions.rejectAdjustment(rejectModal.id, rejectModal.remark || '')
    setRejectModal({ open: false, id: null, remark: '' })
  }

  const returnTo = `${location.pathname}${location.search}`

  const selectableCount = state.rows.filter((r) => r.approvalStatus === 'PENDING' && r.canApprove).length
  const isAllSelected = selectableCount > 0 && state.selectedIds.size >= selectableCount

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      <header className="mb-8 rounded-[2rem] bg-surface-container-low px-8 py-8">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Approval Console</p>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Productivity Hours</h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-on-surface-variant md:text-lg">
          Review manual productivity-hour requests and approve only valid business justifications.
        </p>
      </header>

      <div className="mb-6">
        <SharedFilters
          showSearch={true}
          searchField={state.searchField}
          setSearchField={actions.setSearchField}
          searchText={state.searchText}
          setSearchText={actions.setSearchText}
          searchFieldOptions={[
            { value: 'ALL', label: 'All Fields' },
            { value: 'EMPLOYEE', label: 'Employee' },
            { value: 'PROJECT', label: 'Project' },
            { value: 'TASK', label: 'Task' },
          ]}
          showStatus={true}
          statusFilter={state.statusFilter}
          setStatusFilter={actions.setStatusFilter}
          statusOptions={[
            { value: 'PENDING', label: 'Pending' },
            { value: 'ALL', label: 'All Statuses' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
          ]}
          visibleAccordionFilters={['agent', 'project', 'task', 'dateRange']}
          agentFilter={state.userFilter}
          setAgentFilter={actions.setUserFilter}
          agentOptions={state.userOptions.map((option) => ({
            value: String(option.id),
            label: option.name || `User #${option.id}`,
          }))}
          projectFilter={state.projectFilter}
          setProjectFilter={actions.setProjectFilter}
          taskFilter={state.taskFilter}
          setTaskFilter={actions.setTaskFilter}
          fetchProjectOptions={actions.fetchProjectFilterOptions}
          fetchTaskOptions={actions.fetchTaskFilterOptions}
          projectInitialLabel={state.selectedProjectLabel}
          taskInitialLabel={state.selectedTaskLabel}
          fromDate={state.fromDate}
          setFromDate={actions.setFromDate}
          toDate={state.toDate}
          setToDate={actions.setToDate}
          tools={[
            {
              key: 'multiSelect',
              label: state.isMultiSelectMode ? 'Cancel Selection' : 'Select Multiple',
              icon: state.isMultiSelectMode ? 'close' : 'checklist',
              iconClassName: state.isMultiSelectMode ? 'text-error' : 'text-primary',
            }
          ]}
          onToolSelect={(key) => {
            if (key === 'multiSelect') {
              actions.toggleMultiSelectMode();
            }
          }}
        />
      </div>

      {state.error ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}
      {state.actionError ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.actionError}</div>
      ) : null}
      {state.actionMessage ? (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.actionMessage}</div>
      ) : null}

      <section className="overflow-hidden rounded-3xl bg-surface-container-low relative">
        {state.isMultiSelectMode && (
          <div className="bg-surface-container-highest border-b border-outline-variant/30 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-on-surface">
                {state.selectedIds.size} selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={actions.bulkApprove}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-emerald-100 hover:bg-emerald-200 px-6 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50 transition"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={actions.bulkReject}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-error-container hover:bg-error-container/80 px-6 py-2 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50 transition"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={actions.selectAll}
                disabled={selectableCount === 0}
                className="rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-6 py-2 text-xs font-bold uppercase tracking-wider text-white transition shadow-xs"
              >
                {isAllSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1320px] border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-surface-container-highest/40">
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Employee</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Date</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Project</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Task</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Hours / Productivity Details</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Approval</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Decision Remark</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-center">Actions</th>
                {state.isMultiSelectMode && (
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-right">Select</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {state.isLoading ? (
                <tr>
                  <td colSpan={state.isMultiSelectMode ? 9 : 8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading approval queue...
                  </td>
                </tr>
              ) : !state.error && state.rows.length === 0 ? (
                <tr>
                  <td colSpan={state.isMultiSelectMode ? 9 : 8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    <ApprovalEmptyState />
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const isPending = row.approvalStatus === 'PENDING'
                  const isBusy = state.activeActionId === row.id
                  return (
                    <tr key={row.id} className="transition-colors hover:bg-surface-container-lowest">
                      <td className="px-6 py-5">
                        <div className="text-base font-semibold text-on-surface">{row.userName}</div>
                        <div className="mt-1 text-xs text-on-surface-variant">{row.departmentName || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-5 text-sm text-on-surface-variant">{formatApprovalDateTime(row.createdAt)}</td>
                      <td className="px-6 py-5">
                        <div className="text-sm font-semibold text-on-surface">{row.projectName || 'N/A'}</div>
                        {row.projectCode ? <div className="mt-1 text-xs text-on-surface-variant">{row.projectCode}</div> : null}
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-sm font-semibold text-on-surface">{row.taskName || 'N/A'}</div>
                        {row.taskCode ? <div className="mt-1 text-xs text-on-surface-variant">{row.taskCode}</div> : null}
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-base font-bold text-on-surface">{row.manualActiveLabel}</div>
                        <div className="mt-1 text-xs font-semibold text-primary">{row.reasonLabel}</div>
                        <div className="mt-1 max-w-[240px] text-sm text-on-surface-variant">{row.remark}</div>
                      </td>
                      <td className="px-6 py-5">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${productivityStatusChipClass(row.approvalStatus)}`}>
                          {row.approvalStatus}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-on-surface-variant">{row.decisionRemark}</td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end gap-2">
                          {!state.isMultiSelectMode && (
                            <>
                              <Link
                                to={`/approval/productivity-hours/view?id=${row.id}`}
                                state={{ returnTo }}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary-container text-on-secondary-container"
                                title="View productivity details"
                              >
                                <span className="material-symbols-outlined text-base">visibility</span>
                              </Link>
                              {row.canEditRequestedTime && isPending ? (
                                <button
                                  type="button"
                                  disabled={isBusy}
                                  onClick={() => setEditItem(row)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-fixed text-primary disabled:opacity-50"
                                  title="Edit requested time"
                                >
                                  <span className="material-symbols-outlined text-base">edit</span>
                                </button>
                              ) : null}
                              {row.canApprove && isPending ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => actions.approveAdjustment(row.id)}
                                    className="rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50"
                                  >
                                    {isBusy ? 'Working...' : 'Approve'}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => setRejectModal({ open: true, id: row.id, remark: '' })}
                                    className="rounded-full bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50"
                                  >
                                    {isBusy ? 'Working...' : 'Reject'}
                                  </button>
                                </>
                              ) : (
                                <span />
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      {state.isMultiSelectMode && (
                        <td className="px-6 py-5 text-right">
                          {row.canApprove && isPending ? (
                            <input
                              type="checkbox"
                              checked={state.selectedIds.has(row.id)}
                              onChange={() => actions.toggleSelection(row.id)}
                              className="h-5 w-5 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer"
                            />
                          ) : (
                            <input
                              type="checkbox"
                              disabled
                              className="h-5 w-5 rounded border-outline-variant opacity-30 cursor-not-allowed"
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/20 px-6 py-5">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart}-{state.rangeEnd} of {state.totalCount} adjustment entries
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Pending</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.pending}</h3>
        </article>
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Approved</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.approved}</h3>
        </article>
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Rejected</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.rejected}</h3>
        </article>
      </section>

      {rejectModal.open ? (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl">
            <h4 className="text-xl font-bold text-on-surface">Reject Productivity Adjustment</h4>
            <p className="mt-2 text-sm text-on-surface-variant">
              Add an optional decision remark for audit context.
            </p>
            <label className="mt-4 block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                Decision Remark
              </span>
              <textarea
                rows={4}
                value={rejectModal.remark}
                onChange={(event) => setRejectModal((prev) => ({ ...prev, remark: event.target.value }))}
                className="w-full resize-none rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
                placeholder="Optional remark..."
              />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRejectModal({ open: false, id: null, remark: '' })}
                className="rounded-md bg-secondary-container px-5 py-2 text-sm font-bold text-on-secondary-container"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReject}
                disabled={state.activeActionId === rejectModal.id}
                className="rounded-md bg-error px-5 py-2 text-sm font-bold text-on-error disabled:opacity-60"
              >
                {state.activeActionId === rejectModal.id ? 'Submitting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ProductivityAdjustmentEditModal
        item={editItem}
        isSaving={Boolean(editItem && state.activeActionId === editItem.id)}
        onClose={() => setEditItem(null)}
        onSubmit={async (payload) => {
          await actions.editRequestedTime(editItem.id, payload)
          setEditItem(null)
        }}
      />
    </div>
  )
}
